/**
 * SkillCategoryChart Component
 * 
 * Advanced visualization of skill categories and their development over time.
 * Offers multiple chart types for exploring skill progression across career phases.
 */
import React, { useState } from 'react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell
} from 'recharts';
import { skillCategories, skillGrowthTimeline } from '../../data/skillCategoryData';

// Transform data for various chart types
const transformDataForCharts = () => {
  // Create data for line and area charts
  const timelineData = skillGrowthTimeline.map(period => {
    const dataPoint = { 
      name: period.period,
      role: period.role
    };

    skillCategories.forEach(category => {
      dataPoint[category.name] = period[category.name];
    });

    return dataPoint;
  });

  // Create data for radar chart
  const radarData = skillCategories.map(category => {
    const radarPoint = {
      subject: category.name
    };

    skillGrowthTimeline.forEach(period => {
      radarPoint[period.period] = period[category.name];
    });

    radarPoint.fullMark = 100;
    return radarPoint;
  });

  // Create data for category comparison
  const currentSkills = skillCategories.map(category => ({
    name: category.name,
    value: skillGrowthTimeline[skillGrowthTimeline.length - 1][category.name],
    color: category.color
  }));

  return { timelineData, radarData, currentSkills };
};

const { timelineData, radarData, currentSkills } = transformDataForCharts();

const chartTypes = [
  { id: 'line', name: 'Timeline View', description: 'Shows skill progression over time' },
  { id: 'area', name: 'Growth Areas', description: 'Emphasizes the growth in each skill category' },
  { id: 'radar', name: 'Skill Balance', description: 'Shows skill distribution across categories' },
  { id: 'bar', name: 'Current Skills', description: 'Compares current skill levels by category' }
];

const SkillCategoryChart = () => {
  const [chartType, setChartType] = useState('line');
  const [hoveredCategory, setHoveredCategory] = useState(null);
  
  // Custom tooltip for the line and area charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const period = skillGrowthTimeline.find(p => p.period === label);
      
      return (
        <div className="bg-white p-4 shadow-md rounded border border-gray-200 max-w-sm">
          <p className="font-bold text-sm">{label}: {period?.role}</p>
          <div className="mt-2 space-y-1">
            {payload.map((entry, index) => (
              <div key={`tooltip-${index}`} className="flex justify-between gap-4">
                <span className="text-sm" style={{ color: entry.color }}>
                  {entry.name}:
                </span>
                <span className="text-sm font-medium">
                  {entry.value}%
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Handle hover on category legend
  const handleLegendMouseEnter = (category) => {
    setHoveredCategory(category);
  };

  const handleLegendMouseLeave = () => {
    setHoveredCategory(null);
  };
  
  // Custom legend formatter to show proper names
  const renderLegend = (props) => {
    const { payload } = props;
    
    return (
      <div className="flex flex-wrap justify-center gap-3 mt-2">
        {payload.map((entry, index) => (
          <div 
            key={`legend-${index}`} 
            className={`flex items-center gap-1 px-2 py-1 rounded-full cursor-pointer transition-all ${hoveredCategory === entry.value ? 'bg-gray-100' : ''}`}
            onMouseEnter={() => handleLegendMouseEnter(entry.value)}
            onMouseLeave={handleLegendMouseLeave}
          >
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            ></div>
            <span className="text-xs">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  // Render different chart types
  const renderChart = () => {
    switch(chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={timelineData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis 
                domain={[0, 100]} 
                label={{ value: 'Skill Level (%)', angle: -90, position: 'insideLeft' }} 
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend content={renderLegend} />
              
              {skillCategories.map((category) => (
                <Line 
                  key={category.id}
                  type="monotone" 
                  dataKey={category.name} 
                  stroke={category.color} 
                  strokeWidth={hoveredCategory === category.name ? 3 : 2}
                  strokeOpacity={hoveredCategory && hoveredCategory !== category.name ? 0.3 : 1}
                  activeDot={{ r: 6 }} 
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={timelineData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis domain={[0, 100]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend content={renderLegend} />
              
              {skillCategories.map((category, index) => (
                <Area 
                  key={category.id}
                  type="monotone" 
                  dataKey={category.name} 
                  stackId={index + 1}
                  stroke={category.color} 
                  fill={category.color} 
                  fillOpacity={hoveredCategory === category.name ? 0.8 : 0.6}
                  strokeOpacity={hoveredCategory && hoveredCategory !== category.name ? 0.3 : 1}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );
        
      case 'radar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart outerRadius={150} data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
              <PolarRadiusAxis domain={[0, 100]} />
              <Tooltip />
              <Legend />
              
              {skillGrowthTimeline.slice().reverse().map((period, index) => {
                // Generate distinct colors for each period
                const colors = [
                  { stroke: "#FF3E8A", fill: "#FF3E8A" }, // Current (Mad Magenta)
                  { stroke: "#00A9E0", fill: "#00A9E0" }, // Previous (Scientific Cyan)
                  { stroke: "#57CC99", fill: "#57CC99" }, // Earlier (Flash Green)
                  { stroke: "#FF9B42", fill: "#FF9B42" }, // Earlier (Experiment Orange)
                  { stroke: "#8884d8", fill: "#8884d8" }, // Earlier (Purple)
                  { stroke: "#2D3142", fill: "#2D3142" }, // Earlier (Laboratory Dark)
                  { stroke: "#6B7280", fill: "#6B7280" }  // Earliest (Gray)
                ];
                
                const colorIndex = Math.min(index, colors.length - 1);
                const { stroke, fill } = colors[colorIndex];
                
                return (
                  <Radar 
                    key={period.period}
                    name={period.period} 
                    dataKey={period.period} 
                    stroke={stroke}
                    fill={fill}
                    fillOpacity={index === 0 ? 0.5 : 0.2} 
                  />
                );
              })}
            </RadarChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={currentSkills}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              layout="vertical"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 100]} />
              <YAxis 
                type="category" 
                dataKey="name" 
                width={150}
                tick={{ fontSize: 12 }}
              />
              <Tooltip />
              <Legend />
              <Bar 
                dataKey="value" 
                name="Current Level"
                radius={[0, 4, 4, 0]}
              >
                {currentSkills.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
      
      default:
        return <div>Select a chart type</div>;
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h4 className="font-bold text-slate-700 mb-2">
          Skill Category Visualization
        </h4>
        <div className="flex flex-wrap gap-2">
          {chartTypes.map(type => (
            <button
              key={type.id}
              onClick={() => setChartType(type.id)}
              className={`px-3 py-1 rounded-full text-sm ${
                chartType === type.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
              title={type.description}
            >
              {type.name}
            </button>
          ))}
        </div>
      </div>
      
      <div className="bg-white p-4 rounded-lg border shadow-sm">
        {renderChart()}
      </div>
      
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {skillCategories.map(category => (
          <div 
            key={category.id}
            className="p-4 rounded-lg border hover:shadow-md transition-all"
            style={{ borderLeftWidth: '4px', borderLeftColor: category.color }}
          >
            <h5 className="font-bold mb-2" style={{ color: category.color }}>
              {category.name}
            </h5>
            <p className="text-sm text-slate-600 mb-3">{category.description}</p>
            <div className="flex flex-wrap gap-2">
              {category.skills.map((skill, index) => (
                <span 
                  key={index}
                  className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SkillCategoryChart; 